import { Link } from 'react-router-dom';
import SEOHead from '../../components/seo/SEOHead';
import SchemaOrg from '../../components/seo/SchemaOrg';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import BlogCard from '../../components/base/BlogCard';
import { blogPosts } from '../../mocks/blog-posts';

export default function CIAPReformaTributariaPage() {
  const relatedPosts = blogPosts.filter(post => 
    post.category === 'Reforma Tributária' && 
    post.slug !== 'ciap-reforma-tributaria'
  ).slice(0, 3);

  return (
    <>
      <SEOHead
        title="CIAP na Reforma Tributária: Crédito do Ativo Imobilizado com IBS e CBS | Contabilidade Moderna"
        description="Entenda como funciona o CIAP no contexto da Nova Reforma Tributária. Transição do ICMS para IBS e CBS, crédito imediato, impactos contábeis e fiscais para empresas."
        keywords="CIAP Reforma Tributária, Crédito Ativo Imobilizado IBS, CIAP ICMS x IBS, Crédito imediato IBS CBS, CIAP IVA dual, Reforma Tributária ativos"
        canonicalUrl="https://contabilidademoderna.com.br/artigo/ciap-reforma-tributaria"
        ogType="article"
        ogImage="https://public.readdy.ai/ai/img_res/a9c42ec9b8c7be027b4980c36b9587d9.jpg"
      />

      <SchemaOrg
        type="Article"
        data={{
          headline: "CIAP na Reforma Tributária: como ficam os créditos do ativo imobilizado com IBS e CBS",
          description: "Entenda o fim do modelo tradicional do CIAP do ICMS e a nova lógica de creditamento imediato no sistema de IVA dual.",
          author: { "@type": "Organization", name: "Contabilidade Moderna" },
          publisher: {
            "@type": "Organization",
            name: "Contabilidade Moderna",
            logo: { "@type": "ImageObject", url: "https://contabilidademoderna.com.br/logo.png" }
          },
          datePublished: "2025-01-15",
          dateModified: "2025-01-15",
          image: "https://readdy.ai/api/search-image?query=modern%20industrial%20facility%20with%20advanced%20machinery%20equipment%20and%20technology%20infrastructure%20representing%20fixed%20assets%20and%20capital%20investments%20in%20professional%20corporate%20environment%20with%20clean%20organized%20layout&width=1200&height=630&seq=ciap-hero-001&orientation=landscape"
        }}
      />

      <Navbar />

      <main className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-24">
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
          <div className="absolute inset-0 opacity-10">
            <img 
              src="https://readdy.ai/api/search-image?query=modern%20industrial%20facility%20with%20advanced%20machinery%20equipment%20and%20technology%20infrastructure%20representing%20fixed%20assets%20and%20capital%20investments%20in%20professional%20corporate%20environment%20with%20clean%20organized%20layout&width=1920&height=800&seq=ciap-hero-002&orientation=landscape"
              alt="Ativos Imobilizados"
              className="w-full h-full object-cover"
            />
          </div>
          
          <div className="relative max-w-5xl mx-auto px-6">
            <div className="inline-block bg-teal-500/20 backdrop-blur-sm border border-teal-400/30 rounded-full px-5 py-2 mb-6">
              <span className="text-teal-300 text-sm font-semibold">Reforma Tributária</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              CIAP na Reforma Tributária: como ficam os créditos do ativo imobilizado com IBS e CBS
            </h1>
            
            <p className="text-xl text-slate-300 mb-8 leading-relaxed max-w-3xl">
              Entenda o fim do modelo tradicional do CIAP do ICMS e a nova lógica de creditamento imediato no sistema de IVA dual.
            </p>

            <div className="flex items-center gap-6 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <i className="ri-calendar-line"></i>
                <span>15 de janeiro de 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line"></i>
                <span>12 min de leitura</span>
              </div>
            </div>
          </div>
        </section>

        {/* Introdução */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">O papel do CIAP no sistema tributário</h2>
            
            <p className="text-slate-700 leading-relaxed mb-4">
              O <strong>CIAP (Controle de Crédito do ICMS do Ativo Permanente)</strong> sempre foi um dos temas mais críticos e complexos para empresas intensivas em capital. No modelo atual do ICMS, o crédito tributário sobre aquisições de máquinas, equipamentos e outros ativos imobilizados é apropriado de forma fracionada ao longo de 48 meses, exigindo controles rigorosos e gerando diversos desafios operacionais.
            </p>

            <p className="text-slate-700 leading-relaxed mb-4">
              Com a <strong>Reforma Tributária (EC 132/23)</strong>, essa lógica muda radicalmente. A transição do ICMS para o novo sistema de <strong>IBS (Imposto sobre Bens e Serviços)</strong> e <strong>CBS (Contribuição sobre Bens e Serviços)</strong> elimina a complexidade do CIAP como ele era conhecido, introduzindo o conceito de <strong>creditamento imediato e integral</strong>.
            </p>

            <div className="bg-teal-50 border-l-4 border-teal-500 p-6 my-8 rounded-r-lg">
              <p className="text-slate-800 font-semibold mb-2">
                <i className="ri-information-line text-teal-600 mr-2"></i>
                Importante:
              </p>
              <p className="text-slate-700">
                A reforma não elimina o conceito de crédito tributário sobre ativos, mas elimina a complexidade burocrática e o parcelamento forçado que caracterizavam o CIAP do ICMS.
              </p>
            </div>
          </div>
        </section>

        {/* O que é o CIAP no modelo atual */}
        <section className="bg-slate-50 py-16">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">O que é o CIAP no modelo atual (ICMS)</h2>
            
            <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">Características do CIAP Atual</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-1"></i>
                      <span className="text-slate-700"><strong>Crédito apropriado em 48 parcelas mensais</strong></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-1"></i>
                      <span className="text-slate-700"><strong>Obrigatoriedade de controle individualizado</strong> por bem</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-1"></i>
                      <span className="text-slate-700"><strong>Estorno proporcional</strong> em caso de venda, baixa ou mudança de uso</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-1"></i>
                      <span className="text-slate-700"><strong>Escrituração obrigatória</strong> no Livro de Controle de Crédito de ICMS do Ativo Permanente</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">Problemas do Modelo Atual</h3>
                  <div className="space-y-3">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-time-line text-red-600"></i>
                        <strong className="text-red-900">Crédito lento</strong>
                      </div>
                      <p className="text-sm text-red-800">Recuperação em 4 anos impacta o fluxo de caixa</p>
                    </div>

                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-money-dollar-circle-line text-red-600"></i>
                        <strong className="text-red-900">Alto custo de controle</strong>
                      </div>
                      <p className="text-sm text-red-800">Sistemas, pessoal e processos dedicados</p>
                    </div>

                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-alert-line text-red-600"></i>
                        <strong className="text-red-900">Risco fiscal elevado</strong>
                      </div>
                      <p className="text-sm text-red-800">Glosas frequentes em fiscalizações</p>
                    </div>

                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-settings-3-line text-red-600"></i>
                        <strong className="text-red-900">Complexidade operacional</strong>
                      </div>
                      <p className="text-sm text-red-800">Reconciliações manuais e controles paralelos</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mudança estrutural */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">A mudança estrutural com a Reforma Tributária</h2>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-slate-700 leading-relaxed mb-6">
              Com a <strong>Emenda Constitucional 132/23</strong> e a implementação do novo IVA dual brasileiro, o sistema tributário sobre o consumo passa por uma transformação radical:
            </p>

            <div className="grid md:grid-cols-3 gap-6 my-8">
              <div className="bg-slate-900 text-white rounded-xl p-6 text-center">
                <i className="ri-close-circle-line text-4xl text-red-400 mb-3"></i>
                <h3 className="font-bold text-lg mb-2">ICMS é extinto</h3>
                <p className="text-sm text-slate-300">Fim do modelo estadual fragmentado</p>
              </div>

              <div className="bg-teal-600 text-white rounded-xl p-6 text-center">
                <i className="ri-government-line text-4xl mb-3"></i>
                <h3 className="font-bold text-lg mb-2">Surge o IBS</h3>
                <p className="text-sm text-teal-100">Estados e Municípios</p>
              </div>

              <div className="bg-teal-600 text-white rounded-xl p-6 text-center">
                <i className="ri-bank-line text-4xl mb-3"></i>
                <h3 className="font-bold text-lg mb-2">Surge a CBS</h3>
                <p className="text-sm text-teal-100">União Federal</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl p-8 my-8 text-center">
              <i className="ri-lightbulb-flash-line text-5xl mb-4"></i>
              <h3 className="text-2xl font-bold mb-3">Mensagem-chave</h3>
              <p className="text-xl leading-relaxed">
                O conceito clássico de CIAP deixa de existir como obrigação acessória fracionada. O sistema passa a adotar <strong>não cumulatividade plena</strong> com creditamento imediato.
              </p>
            </div>
          </div>
        </section>

        {/* Como funciona o crédito */}
        <section className="bg-gradient-to-br from-teal-50 to-slate-50 py-16">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Como funciona o crédito do ativo imobilizado no IBS e na CBS</h2>
            
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-flashlight-fill text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">Crédito Integral e Imediato</h3>
                  <p className="text-slate-700 text-lg leading-relaxed">
                    No novo sistema, o crédito tributário sobre aquisições de ativos imobilizados passa a ser <strong>integral e imediato</strong>. Não há mais parcelamento em 48 meses.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mt-8">
                <div className="border-l-4 border-teal-600 pl-6">
                  <h4 className="font-bold text-slate-900 mb-3">Ativos que geram crédito total:</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-slate-700">
                      <i className="ri-check-line text-teal-600"></i>
                      Máquinas e equipamentos industriais
                    </li>
                    <li className="flex items-center gap-2 text-slate-700">
                      <i className="ri-check-line text-teal-600"></i>
                      Instalações e infraestrutura produtiva
                    </li>
                    <li className="flex items-center gap-2 text-slate-700">
                      <i className="ri-check-line text-teal-600"></i>
                      Tecnologia da informação (hardware)
                    </li>
                    <li className="flex items-center gap-2 text-slate-700">
                      <i className="ri-check-line text-teal-600"></i>
                      Veículos de uso operacional
                    </li>
                    <li className="flex items-center gap-2 text-slate-700">
                      <i className="ri-check-line text-teal-600"></i>
                      Outros ativos produtivos
                    </li>
                  </ul>
                </div>

                <div className="border-l-4 border-teal-600 pl-6">
                  <h4 className="font-bold text-slate-900 mb-3">O crédito pode ser:</h4>
                  <div className="space-y-3">
                    <div className="bg-teal-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <i className="ri-exchange-line text-teal-600"></i>
                        <strong className="text-slate-900">Compensado</strong>
                      </div>
                      <p className="text-sm text-slate-700">Com débitos de IBS/CBS da própria empresa</p>
                    </div>

                    <div className="bg-teal-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <i className="ri-refund-2-line text-teal-600"></i>
                        <strong className="text-slate-900">Ressarcido</strong>
                      </div>
                      <p className="text-sm text-slate-700">Devolução em dinheiro quando aplicável</p>
                    </div>

                    <div className="bg-teal-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <i className="ri-share-forward-line text-teal-600"></i>
                        <strong className="text-slate-900">Transferido</strong>
                      </div>
                      <p className="text-sm text-slate-700">Conforme regras do novo sistema</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Comparativo antes e depois */}
        <section className="max-w-6xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">O que muda na prática para as empresas</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Antes */}
            <div className="bg-slate-900 rounded-xl p-8 text-white">
              <div className="flex items-center gap-3 mb-6">
                <i className="ri-time-line text-3xl text-slate-400"></i>
                <h3 className="text-2xl font-bold">Antes (ICMS / CIAP)</h3>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-red-400 mt-1 flex-shrink-0"></i>
                  <div>
                    <strong className="block mb-1">Crédito em 48 meses</strong>
                    <p className="text-sm text-slate-300">Apropriação fracionada ao longo de 4 anos</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-red-400 mt-1 flex-shrink-0"></i>
                  <div>
                    <strong className="block mb-1">Controle físico-contábil complexo</strong>
                    <p className="text-sm text-slate-300">Livros, sistemas e reconciliações manuais</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-red-400 mt-1 flex-shrink-0"></i>
                  <div>
                    <strong className="block mb-1">Alto risco de glosa</strong>
                    <p className="text-sm text-slate-300">Fiscalizações frequentes e autuações</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-red-400 mt-1 flex-shrink-0"></i>
                  <div>
                    <strong className="block mb-1">Baixa eficiência financeira</strong>
                    <p className="text-sm text-slate-300">Impacto negativo no fluxo de caixa</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Agora */}
            <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl p-8 text-white">
              <div className="flex items-center gap-3 mb-6">
                <i className="ri-flashlight-fill text-3xl"></i>
                <h3 className="text-2xl font-bold">Agora (IBS / CBS)</h3>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-200 mt-1 flex-shrink-0 text-xl"></i>
                  <div>
                    <strong className="block mb-1">Crédito imediato</strong>
                    <p className="text-sm text-teal-100">Apropriação integral no momento da aquisição</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-200 mt-1 flex-shrink-0 text-xl"></i>
                  <div>
                    <strong className="block mb-1">Fim do CIAP como controle fracionado</strong>
                    <p className="text-sm text-teal-100">Simplificação radical dos processos</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-200 mt-1 flex-shrink-0 text-xl"></i>
                  <div>
                    <strong className="block mb-1">Maior previsibilidade de caixa</strong>
                    <p className="text-sm text-teal-100">Recuperação imediata do crédito tributário</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-200 mt-1 flex-shrink-0 text-xl"></i>
                  <div>
                    <strong className="block mb-1">Estímulo ao investimento</strong>
                    <p className="text-sm text-teal-100">Redução do custo efetivo de CAPEX</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Impactos */}
        <section className="bg-slate-50 py-16">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Impactos contábeis, fiscais e financeiros</h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {/* Contábeis */}
              <div className="bg-white rounded-xl shadow-sm p-8">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-bar-chart-box-line text-3xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">📊 Contábeis</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-blue-600 mt-1 flex-shrink-0"></i>
                    <span className="text-slate-700">Alinhamento com CPC 27 / IAS 16</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-blue-600 mt-1 flex-shrink-0"></i>
                    <span className="text-slate-700">Redução de controles paralelos</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-blue-600 mt-1 flex-shrink-0"></i>
                    <span className="text-slate-700">Menos reconciliações manuais</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-blue-600 mt-1 flex-shrink-0"></i>
                    <span className="text-slate-700">Maior confiabilidade dos dados</span>
                  </li>
                </ul>
              </div>

              {/* Financeiros */}
              <div className="bg-white rounded-xl shadow-sm p-8">
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-money-dollar-circle-line text-3xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">💰 Financeiros</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-green-600 mt-1 flex-shrink-0"></i>
                    <span className="text-slate-700">Melhora no fluxo de caixa</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-green-600 mt-1 flex-shrink-0"></i>
                    <span className="text-slate-700">Redução do custo efetivo do investimento</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-green-600 mt-1 flex-shrink-0"></i>
                    <span className="text-slate-700">Decisão de CAPEX mais racional</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-green-600 mt-1 flex-shrink-0"></i>
                    <span className="text-slate-700">Impacto positivo no valuation</span>
                  </li>
                </ul>
              </div>

              {/* Fiscais */}
              <div className="bg-white rounded-xl shadow-sm p-8">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-scales-3-line text-3xl text-purple-600"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">⚖️ Fiscais</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-purple-600 mt-1 flex-shrink-0"></i>
                    <span className="text-slate-700">Menor risco de autuação</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-purple-600 mt-1 flex-shrink-0"></i>
                    <span className="text-slate-700">Redução de litígios</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-purple-600 mt-1 flex-shrink-0"></i>
                    <span className="text-slate-700">Sistema mais transparente</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-purple-600 mt-1 flex-shrink-0"></i>
                    <span className="text-slate-700">Conformidade simplificada</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Período de transição */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">Atenção ao período de transição</h2>
          
          <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-xl p-8 mb-8">
            <div className="flex items-start gap-4">
              <i className="ri-alert-line text-3xl text-amber-600 flex-shrink-0"></i>
              <div>
                <h3 className="text-xl font-bold text-amber-900 mb-3">Importante: CIAP do ICMS continua durante a transição</h3>
                <p className="text-amber-800 leading-relaxed mb-4">
                  O modelo atual do CIAP não desaparece imediatamente. Durante o período de transição (2026-2032), as empresas precisarão manter os controles existentes enquanto o novo sistema é implementado gradualmente.
                </p>
              </div>
            </div>
          </div>

          <div className="prose prose-lg max-w-none">
            <div className="bg-white rounded-xl shadow-sm p-8 mb-6">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Créditos acumulados de ICMS:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-circle-fill text-teal-600 mt-1 flex-shrink-0"></i>
                  <span className="text-slate-700">Serão <strong>compensados com o IBS</strong> durante o período de transição</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-circle-fill text-teal-600 mt-1 flex-shrink-0"></i>
                  <span className="text-slate-700">Compensação em <strong>até 240 parcelas</strong>, a partir de 2033</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-circle-fill text-teal-600 mt-1 flex-shrink-0"></i>
                  <span className="text-slate-700">Empresas precisam manter <strong>controle adequado dos ativos</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-circle-fill text-teal-600 mt-1 flex-shrink-0"></i>
                  <span className="text-slate-700"><strong>Rastreabilidade dos créditos existentes</strong> é fundamental</span>
                </li>
              </ul>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <i className="ri-error-warning-line text-2xl text-red-600 flex-shrink-0"></i>
                <div>
                  <h4 className="font-bold text-red-900 mb-2">Alerta importante:</h4>
                  <p className="text-red-800">
                    A transição exige <strong>organização patrimonial e fiscal rigorosa</strong> para evitar perda de créditos. Empresas que não mantiverem controles adequados podem perder milhões em créditos acumulados.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Boas práticas */}
        <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-16">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl font-bold mb-8 text-center">Boas práticas recomendadas</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-line text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">Revisar cadastro de ativos imobilizados</h3>
                    <p className="text-slate-300 text-sm">Garantir que todos os ativos estejam corretamente cadastrados e classificados</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-line text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">Garantir vínculo entre ativo físico, contábil e fiscal</h3>
                    <p className="text-slate-300 text-sm">Reconciliação completa entre inventário físico e registros contábeis</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-line text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">Revisar políticas de CAPEX</h3>
                    <p className="text-slate-300 text-sm">Ajustar critérios de investimento considerando o crédito imediato</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-line text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">Avaliar impacto do crédito imediato no valuation</h3>
                    <p className="text-slate-300 text-sm">Recalcular o valor presente líquido dos investimentos</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-line text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">Preparar sistemas (ERP, fiscal e patrimonial)</h3>
                    <p className="text-slate-300 text-sm">Adequar tecnologia para IBS/CBS e novo modelo de creditamento</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-line text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">Capacitar equipes contábeis e fiscais</h3>
                    <p className="text-slate-300 text-sm">Treinamento sobre o novo modelo e suas implicações práticas</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Conclusão */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">Conclusão</h2>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-slate-700 leading-relaxed mb-4">
              A <strong>Reforma Tributária simplifica radicalmente o crédito do ativo imobilizado</strong>. O que antes era um dos temas mais complexos e custosos da gestão tributária brasileira passa a ser tratado de forma direta e eficiente no novo sistema de IBS e CBS.
            </p>

            <p className="text-slate-700 leading-relaxed mb-4">
              O <strong>CIAP deixa de ser um gargalo operacional</strong> e se transforma em um mecanismo simples de creditamento imediato, alinhado às melhores práticas internacionais de IVA.
            </p>

            <p className="text-slate-700 leading-relaxed mb-4">
              <strong>Empresas preparadas capturam ganhos financeiros reais:</strong> melhora no fluxo de caixa, redução de custos operacionais, menor risco fiscal e decisões de investimento mais racionais.
            </p>

            <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl p-8 my-8">
              <p className="text-xl leading-relaxed text-center">
                <strong>O ativo imobilizado passa a ser um vetor estratégico, não um passivo fiscal.</strong>
              </p>
            </div>

            <p className="text-slate-700 leading-relaxed">
              A transição exige planejamento, mas os benefícios de longo prazo são inequívocos. Empresas que se anteciparem e estruturarem adequadamente seus controles patrimoniais e fiscais estarão em posição privilegiada para aproveitar as vantagens do novo sistema.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-br from-teal-600 to-teal-700 py-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <i className="ri-customer-service-2-line text-6xl text-white mb-6"></i>
            <h2 className="text-3xl font-bold text-white mb-4">
              Quer entender como a Reforma Tributária impacta seus ativos e investimentos?
            </h2>
            <p className="text-xl text-teal-100 mb-8">
              Nossa equipe de especialistas pode ajudar sua empresa a se preparar para o novo sistema de IBS e CBS
            </p>
            <Link 
              to="/contato"
              className="inline-block bg-white text-teal-700 px-8 py-4 rounded-lg font-bold text-lg hover:bg-teal-50 transition-colors whitespace-nowrap"
            >
              Fale com nossos especialistas
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </section>

        {/* Artigos relacionados */}
        {relatedPosts.length > 0 && (
          <section className="max-w-6xl mx-auto px-6 py-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-8">Artigos relacionados</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedPosts.map((post) => (
                <BlogCard key={post.slug} post={post} />
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}